import { useState, useEffect, useRef } from 'react'
import '../Styles/Chatbot.css'

const WEB3FORMS_KEY = process.env.REACT_APP_WEB3FORMS_KEY

const STEPS = {
  WELCOME: 'welcome',
  SERVICES: 'services',
  ASK_DEVELOP: 'ask_develop',
  ASK_EMAIL: 'ask_email',
  ASK_PHONE: 'ask_phone',
  ASK_COMPANY: 'ask_company',
  DONE: 'done',
  CLOSED: 'closed',
}

export default function ChatBot() {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(STEPS.WELCOME)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [userData, setUserData] = useState({ email: '', phone: '', company: '', interest: '' })
  const [typing, setTyping] = useState(false)
  const messagesEnd = useRef(null)
  const hasOpened = useRef(false)

  const botReply = (text, options, delay = 700) => {
    setTyping(true)
    setTimeout(() => {
      setTyping(false)
      setMessages(prev => [...prev, { from: 'bot', text, options }])
    }, delay)
  }

  const userReply = (text) => {
    setMessages(prev => [...prev, { from: 'user', text }])
  }

  useEffect(() => {
    if (hasOpened.current) return
    hasOpened.current = true
    // Do not auto-open on small/medium screens — it blocks page content
    if (window.innerWidth <= 768) return
    setTimeout(() => {
      setOpen(true)
      setMessages([{ from: 'bot', text: '👋 Welcome to Shanti Group!' }])
      setTimeout(() => {
        setMessages(prev => [...prev, {
          from: 'bot',
          text: 'How can we help you today?',
          options: [
            { label: '⚡ Explore Our Solutions', value: 'explore' },
            { label: '💬 Talk to an Expert', value: 'talk' },
          ],
        }])
      }, 1000)
    }, 2000)
  }, [])

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, typing])

  const handleOption = (value) => {
    if (step === STEPS.WELCOME) {
      if (value === 'explore') {
        userReply('Explore Our Solutions')
        setStep(STEPS.SERVICES)
        botReply('Great choice! Here are our core solutions:', [
          { label: '🔌 MEP & Electrical Systems', value: 'mep' },
          { label: '☀️ Solar Energy Solutions', value: 'solar' },
          { label: '🏭 Electrical Panels & Automation', value: 'panels' },
          { label: '📋 I need something custom', value: 'more' },
        ])
      } else if (value === 'talk') {
        userReply('Talk to an Expert')
        setUserData(prev => ({ ...prev, interest: 'Talk to an Expert' }))
        setStep(STEPS.ASK_EMAIL)
        botReply('We\'d love to connect! Could you share your email address?')
      }
    } else if (step === STEPS.SERVICES) {
      if (value === 'mep') {
        userReply('MEP & Electrical Systems')
        setUserData(prev => ({ ...prev, interest: 'MEP & Electrical Systems' }))
        setStep(STEPS.ASK_EMAIL)
        botReply('Great interest in MEP solutions! To help you further, could you share your email address?')
      } else if (value === 'solar') {
        userReply('Solar Energy Solutions')
        setUserData(prev => ({ ...prev, interest: 'Solar Energy Solutions' }))
        setStep(STEPS.ASK_EMAIL)
        botReply('Great interest in Solar solutions! To help you further, could you share your email address?')
      } else if (value === 'panels') {
        userReply('Electrical Panels & Automation')
        setUserData(prev => ({ ...prev, interest: 'Electrical Panels & Automation' }))
        setStep(STEPS.ASK_EMAIL)
        botReply('Great interest in Panels & Automation! To help you further, could you share your email address?')
      } else if (value === 'more') {
        userReply('I need something custom')
        setStep(STEPS.ASK_DEVELOP)
        botReply('Are you looking for a custom electrical or energy solution for your business?', [
          { label: '✅ Yes, I\'m interested', value: 'yes' },
          { label: '❌ No, just browsing', value: 'no' },
        ])
      }
    } else if (step === STEPS.ASK_DEVELOP) {
      if (value === 'yes') {
        userReply('Yes, I\'m interested')
        setUserData(prev => ({ ...prev, interest: 'Custom Energy Solution' }))
        setStep(STEPS.ASK_EMAIL)
        botReply('Wonderful! Please share your email address and one of our specialists will get in touch.')
      } else {
        userReply('No, just browsing')
        setStep(STEPS.CLOSED)
        botReply('No worries! Feel free to explore our website. We\'re here if you need anything. 😊', [
          { label: '⚡ Explore Solutions', value: 'restart_explore' },
          { label: '📞 Contact Us', value: 'restart_contact' },
        ])
      }
    } else if (step === STEPS.CLOSED) {
      if (value === 'restart_explore') {
        userReply('Explore Solutions')
        setStep(STEPS.SERVICES)
        botReply('Here are our solutions:', [
          { label: '🔌 MEP & Electrical Systems', value: 'mep' },
          { label: '☀️ Solar Energy Solutions', value: 'solar' },
          { label: '🏭 Electrical Panels & Automation', value: 'panels' },
          { label: '📋 I need something custom', value: 'more' },
        ])
      } else if (value === 'restart_contact') {
        userReply('Contact Us')
        setMessages(prev => [...prev, { from: 'bot', text: 'Taking you to our contact page... 🚀' }])
        setTimeout(() => { window.location.href = '/contact'; setOpen(false) }, 800)
      }
    }
  }

  const isValidEmail = (val) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)
  const isValidPhone = (val) => /^[\d\s+\-()]{7,15}$/.test(val)

  const sendLeadEmail = (data) => {
    fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: 'New Lead from Shanti Group ChatBot',
        from_name: 'Shanti Group Website ChatBot',
        interest: data.interest || 'Not specified',
        email: data.email || 'Not provided',
        phone: data.phone || 'Not provided',
        company: data.company || 'Not provided',
      }),
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const val = input.trim()
    if (!val) return
    setInput('')
    userReply(val)

    if (step === STEPS.ASK_EMAIL) {
      if (isValidEmail(val)) {
        setUserData(prev => ({ ...prev, email: val }))
        setStep(STEPS.ASK_PHONE)
        botReply('Thank you! Could you also share your phone number so we can reach you quickly?')
      } else {
        botReply('That doesn\'t look like a valid email. Please enter a valid email address (e.g. name@company.com).')
      }
    } else if (step === STEPS.ASK_PHONE) {
      if (isValidPhone(val)) {
        setUserData(prev => ({ ...prev, phone: val }))
        setStep(STEPS.ASK_COMPANY)
        botReply('Got it! 📞 What is the name of the company you represent?')
      } else {
        botReply('That doesn\'t look like a valid phone number. Please enter a valid number (e.g. +91 98765 43210).')
      }
    } else if (step === STEPS.ASK_COMPANY) {
      const finalData = { ...userData, company: val }
      setUserData(finalData)
      sendLeadEmail(finalData)
      setStep(STEPS.DONE)
      botReply('Thank you so much! 🙏 A specialist from our team will get in touch with you shortly. Have a great day!')
    }
  }

  const showInput = [STEPS.ASK_EMAIL, STEPS.ASK_PHONE, STEPS.ASK_COMPANY].includes(step)

  const getPlaceholder = () => {
    if (step === STEPS.ASK_EMAIL) return 'e.g. name@company.com'
    if (step === STEPS.ASK_PHONE) return 'e.g. +91 98765 43210'
    if (step === STEPS.ASK_COMPANY) return 'e.g. Acme Corporation'
    return 'Type here...'
  }

  return (
    <>
      <button className="chatbot-toggle" onClick={() => setOpen(!open)} aria-label="Toggle chat">
        {open ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
      </button>

      {open && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <span className="chatbot-header-dot" />
              <span>Shanti Group Assistant</span>
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close chat">✕</button>
          </div>

          <div className="chatbot-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${msg.from}`}>
                {msg.from === 'bot' && <span className="chatbot-avatar">S</span>}
                <div className="chatbot-msg-content">
                  <p>{msg.text}</p>
                  {msg.options && (
                    <div className="chatbot-options">
                      {msg.options.map((opt, j) => (
                        <button key={j} className="chatbot-option-btn" onClick={() => handleOption(opt.value)}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {typing && (
              <div className="chatbot-msg chatbot-msg-bot">
                <span className="chatbot-avatar">S</span>
                <div className="chatbot-msg-content">
                  <div className="chatbot-typing">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEnd} />
          </div>

          {showInput && (
            <form className="chatbot-input" onSubmit={handleSubmit}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={getPlaceholder()}
                autoFocus
              />
              <button type="submit" aria-label="Send">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </button>
            </form>
          )}
        </div>
      )}
    </>
  )
}
